import './styles.css';
import { aiTipCategoryLabels, aiTips, isAiTipUnlocked, purchaseAiTip } from './aiTips';
import { mapRange } from './cosmicMath';
import {
  createAuroraBands,
  createCometSpawn,
  createStarField,
  getAuroraDrift,
  getCometIntervalMs,
  getCometOpacity,
  getDiscoAccentRgb,
  getParallaxShift,
  getStarCountForPointBudget,
  getStarTwinkle,
  getWarpStreakScale,
  type CometSpawn,
  type StarTint,
} from './backdropScene';
import { applyCinematicTimeline, getCinematicIntroProgress } from './cinematicTimeline';
import { createAdaptiveRenderState, getIntenseVisualFeatureCount, getNextAdaptiveRenderState, getOptimizedPixelRatio, getScenePerformanceProfile, shouldRenderFrame } from './renderPerformance';
import { buildWheelGradient, createWheelSpinPlan, getWheelLandingIndex, getWheelSegmentSize, wheelColors } from './colorWheel';
import {
  createRarityFanfare,
  createRevealParticles,
  getRevealAnnouncement,
  getRevealTheme,
  getWheelTickPitch,
  type FanfareNote,
  type RevealTheme,
} from './lootFx';
import {
  applyDebugGemsFromSearch,
  applyRewardToInventory,
  createLootSpinPlan,
  defaultInventory,
  getCollectionProgress,
  isCollectionComplete,
  getLootComboBonus,
  getLootAnimationProfile,
  getEligibleLootRewards,
  getShopItems,
  hasUnlockedFeature,
  getUnlockedFeatureIds,
  getUnlockedFontOptions,
  getUnlockedMenuItems,
  lootRewards,
  purchaseShopReward,
  rarityMeta,
  shouldGrantAkFromSearch,
  type LootInventory,
  type LootReward,
  type LootTickerItem,
} from './lootBox';
import { waitForAnimationCompletion } from './animation';

applyCinematicTimeline();

const wheelButton = document.querySelector<HTMLButtonElement>('[data-color-wheel]');
const wheelDisc = document.querySelector<HTMLElement>('[data-color-wheel-disc]');
const wheelStatus = document.querySelector<HTMLElement>('[data-color-wheel-status]');
const wheelStatusText = document.querySelector<HTMLElement>('[data-color-wheel-status-text]');
const wheelChip = document.querySelector<HTMLElement>('[data-color-wheel-chip]');
const wheelPointer = document.querySelector<HTMLElement>('.color-wheel-pointer');
const lightingWash = document.querySelector<HTMLElement>('[data-lighting-wash]');
let wheelRotation = 0;
let wheelIsSpinning = false;

function getSpinSeed(): number {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return values[0] / 2 ** 32;
}

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window.AudioContext !== 'function') {
    return null;
  }

  audioContext ??= new AudioContext();

  if (audioContext.state === 'suspended') {
    void audioContext.resume();
  }

  return audioContext;
}

function playFanfare(notes: readonly FanfareNote[]) {
  const context = getAudioContext();

  if (!context) {
    return;
  }

  const baseTime = context.currentTime + 0.01;

  for (const note of notes) {
    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    const startAt = baseTime + note.startMs / 1000;
    const stopAt = startAt + note.durationMs / 1000;

    oscillator.type = note.type;
    oscillator.frequency.setValueAtTime(note.frequency, startAt);
    envelope.gain.setValueAtTime(0, startAt);
    envelope.gain.linearRampToValueAtTime(note.gain, startAt + 0.012);
    envelope.gain.exponentialRampToValueAtTime(0.0001, stopAt);
    oscillator.connect(envelope).connect(context.destination);
    oscillator.start(startAt);
    oscillator.stop(stopAt + 0.05);
  }
}

let lastTickSoundAt = 0;

function playTick(frequency: number, gain = 0.026) {
  const now = performance.now();

  if (now - lastTickSoundAt < 40) {
    return;
  }

  lastTickSoundAt = now;
  playFanfare([{ frequency, startMs: 0, durationMs: 46, type: 'square', gain }]);
}

function setWheelStatus(text: string) {
  if (wheelStatusText) {
    wheelStatusText.textContent = text;
  } else if (wheelStatus) {
    wheelStatus.textContent = text;
  }
}

function setWheelChip(hex: string) {
  if (wheelChip) {
    wheelChip.style.background = hex;
    wheelChip.dataset.active = 'true';
  }
}

function pulseLightingWash() {
  if (!lightingWash) {
    return;
  }

  lightingWash.classList.remove('is-pulsing');
  void lightingWash.offsetWidth;
  lightingWash.classList.add('is-pulsing');
}

function setGlobalLightingColor(colorIndex: number) {
  const color = wheelColors[colorIndex];
  document.documentElement.style.setProperty('--wheel-glow', color.hex);
  document.documentElement.style.setProperty('--wheel-glow-rgb', color.rgb);
  document.documentElement.dataset.landingColor = color.name;
  setWheelChip(color.hex);
  return color;
}

function applyGlobalLighting(colorIndex: number) {
  const color = setGlobalLightingColor(colorIndex);
  setWheelStatus(`Locked in: ${color.name}`);
}

function kickWheelPointer() {
  wheelPointer?.animate([
    { transform: 'translateX(-50%) rotate(0deg)' },
    { transform: 'translateX(-50%) rotate(-24deg)', offset: 0.4 },
    { transform: 'translateX(-50%) rotate(0deg)' },
  ], {
    duration: 110,
    easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  });
}

function readRotationDegrees(element: HTMLElement): number {
  const { transform } = getComputedStyle(element);

  if (!transform || transform === 'none') {
    return 0;
  }

  const matrix = new DOMMatrixReadOnly(transform);
  return Math.atan2(matrix.b, matrix.a) * (180 / Math.PI);
}

function startWheelTickLoop(startRotation: number, finalRotation: number): () => void {
  if (!wheelDisc) {
    return () => {};
  }

  const segmentSize = getWheelSegmentSize();
  const totalRotation = Math.max(1, finalRotation - startRotation);
  let active = true;
  let previousAngle = readRotationDegrees(wheelDisc);
  let accumulated = 0;
  let previousSegment = Math.floor(startRotation / segmentSize);

  const sample = () => {
    if (!active) {
      return;
    }

    const angle = readRotationDegrees(wheelDisc);
    let delta = angle - previousAngle;

    if (delta < 0) {
      delta += 360;
    }

    previousAngle = angle;
    accumulated += delta;

    const absoluteRotation = startRotation + accumulated;
    const progress = Math.min(1, accumulated / totalRotation);
    const segment = Math.floor(absoluteRotation / segmentSize);

    if (segment !== previousSegment) {
      previousSegment = segment;
      kickWheelPointer();
      playTick(getWheelTickPitch(progress));

      const passingIndex = getWheelLandingIndex(absoluteRotation);

      if (progress > 0.45) {
        // The home stretch: let the whole page chase the colours gliding under the pointer.
        const color = setGlobalLightingColor(passingIndex);
        setWheelStatus(`…${color.name}…`);
        navigator.vibrate?.(6);
      }
    }

    requestAnimationFrame(sample);
  };

  requestAnimationFrame(sample);

  return () => {
    active = false;
  };
}

async function runColorWheelSpin() {
  if (!wheelButton || !wheelDisc || wheelIsSpinning) {
    return;
  }

  wheelIsSpinning = true;
  wheelButton.disabled = true;
  wheelButton.dataset.spinning = 'true';

  const plan = createWheelSpinPlan(getSpinSeed(), wheelRotation);
  const landingIndex = getWheelLandingIndex(plan.finalRotationDegrees);
  const reduceWheelMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  setWheelStatus('Wheel charging through the spectrum…');

  if (reduceWheelMotion) {
    wheelRotation = plan.finalRotationDegrees;
    wheelDisc.style.transform = `rotate(${wheelRotation}deg)`;
    applyGlobalLighting(landingIndex);
  } else {
    const stopTickLoop = startWheelTickLoop(wheelRotation, plan.finalRotationDegrees);
    const tiltAnimation = wheelButton.animate([
      { transform: 'rotateX(0deg) rotateY(0deg) scale(1)', filter: 'brightness(1) saturate(1)' },
      { transform: 'rotateX(58deg) rotateY(-22deg) scale(1.24)', filter: 'brightness(1.9) saturate(1.85)', offset: 0.28 },
      { transform: 'rotateX(32deg) rotateY(18deg) scale(1.12)', filter: 'brightness(1.55) saturate(1.65)', offset: 0.62 },
      { transform: 'rotateX(0deg) rotateY(0deg) scale(1)', filter: 'brightness(1.1) saturate(1.2)' },
    ], {
      duration: plan.durationMs,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    });
    const spinAnimation = wheelDisc.animate([
      { transform: `rotate(${wheelRotation}deg)` },
      { transform: `rotate(${plan.finalRotationDegrees}deg)` },
    ], {
      duration: plan.durationMs,
      easing: 'cubic-bezier(0.12, 0.78, 0.08, 1)',
      fill: 'forwards',
    });

    await Promise.all([spinAnimation.finished, tiltAnimation.finished]);
    stopTickLoop();
    wheelRotation = plan.finalRotationDegrees;
    wheelDisc.style.transform = `rotate(${wheelRotation}deg)`;
    applyGlobalLighting(landingIndex);
    pulseLightingWash();
    navigator.vibrate?.([12, 40, 24]);
    playFanfare([
      { frequency: 523.25, startMs: 0, durationMs: 140, type: 'triangle', gain: 0.055 },
      { frequency: 783.99, startMs: 110, durationMs: 160, type: 'triangle', gain: 0.06 },
      { frequency: 1046.5, startMs: 230, durationMs: 420, type: 'triangle', gain: 0.065 },
    ]);

    const returnAnimation = wheelButton.animate([
      { transform: 'translate3d(0, 0, 0) rotateZ(0deg) scale(1)' },
      { transform: 'translate3d(-38vw, -22vh, 260px) rotateZ(-240deg) scale(1.58)', offset: 0.34 },
      { transform: 'translate3d(18vw, 28vh, 120px) rotateZ(160deg) scale(0.82)', offset: 0.7 },
      { transform: 'translate3d(0, 0, 0) rotateZ(0deg) scale(1)' },
    ], {
      duration: 1350,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    });

    await returnAnimation.finished;
  }

  wheelButton.dataset.spinning = 'false';
  wheelButton.disabled = false;
  wheelIsSpinning = false;
}

if (wheelButton && wheelDisc) {
  wheelDisc.style.backgroundImage = buildWheelGradient();
  wheelButton.addEventListener('click', () => {
    void runColorWheelSpin();
  });
}


const menuToggle = document.querySelector<HTMLButtonElement>('[data-menu-toggle]');
const menuPanel = document.querySelector<HTMLElement>('[data-unlock-menu]');
const menuList = document.querySelector<HTMLElement>('[data-unlock-menu-list]');
const lootLabel = document.querySelector<HTMLElement>('[data-loot-label]');
const lootToggle = document.querySelector<HTMLButtonElement>('[data-loot-toggle]');
const lootPanel = document.querySelector<HTMLElement>('[data-loot-panel]');
const lootCloseButton = document.querySelector<HTMLButtonElement>('[data-loot-close]');
const shopLauncher = document.querySelector<HTMLElement>('[data-shop-launcher]');
const shopToggle = document.querySelector<HTMLButtonElement>('[data-shop-toggle]');
const shopPanel = document.querySelector<HTMLElement>('[data-shop-panel]');
const shopCloseButton = document.querySelector<HTMLButtonElement>('[data-shop-close]');
const lootLabelDismissButton = document.querySelector<HTMLButtonElement>('[data-loot-label-dismiss]');
const lootButton = document.querySelector<HTMLButtonElement>('[data-loot-box]');
const lootReel = document.querySelector<HTMLElement>('[data-loot-reel]');
const lootResult = document.querySelector<HTMLElement>('[data-loot-result]');
const lootReveal = document.querySelector<HTMLButtonElement>('[data-loot-reveal]');
const lootRevealRarity = document.querySelector<HTMLElement>('[data-loot-reveal-rarity]');
const lootRevealTitle = document.querySelector<HTMLElement>('[data-loot-reveal-title]');
const lootRevealDescription = document.querySelector<HTMLElement>('[data-loot-reveal-description]');
const lootRevealFlavor = document.querySelector<HTMLElement>('[data-loot-reveal-flavor]');
const lootRevealFx = document.querySelector<HTMLElement>('[data-loot-reveal-fx]');
const lootRevealBurst = document.querySelector<HTMLElement>('[data-loot-reveal-burst]');
const lootRevealShockwave = document.querySelector<HTMLElement>('[data-loot-reveal-shockwave]');
const rarityFlash = document.querySelector<HTMLElement>('[data-rarity-flash]');
const appShell = document.querySelector<HTMLElement>('#app');
const gemCount = document.querySelector<HTMLElement>('[data-gem-count]');
const lootCombo = document.querySelector<HTMLElement>('[data-loot-combo]');
const akLauncher = document.querySelector<HTMLElement>('[data-ak-launcher]');
const akEquipButton = document.querySelector<HTMLButtonElement>('[data-ak-equip]');
const shopGrid = document.querySelector<HTMLElement>('[data-shop-grid]');
const shopProgress = document.querySelector<HTMLElement>('[data-shop-progress]');
const shopToggleProgress = document.querySelector<HTMLElement>('[data-shop-toggle-progress]');
const shopGemCount = document.querySelector<HTMLElement>('[data-shop-gem-count]');
const fontSelectorRow = document.querySelector<HTMLElement>('[data-font-selector-row]');
const fontSelector = document.querySelector<HTMLSelectElement>('[data-font-selector]');
const headline = document.querySelector<HTMLElement>('#headline');
const aiTipsGrid = document.querySelector<HTMLElement>('[data-ai-tips-grid]');
const aiGuideStatus = document.querySelector<HTMLElement>('[data-ai-guide-status]');
const completionCredits = document.querySelector<HTMLElement>('[data-completion-credits]');
const completionCloseButton = document.querySelector<HTMLButtonElement>('[data-completion-close]');
const inventoryStorageKey = 'steves-tools-loot-inventory';
const lootLabelStorageKey = 'steves-tools-loot-label-dismissed';
const shotCounts = new WeakMap<Element, number>();
let lootRotation = 0;
let lootIsOpening = false;
let akEquipped = false;
let lootPanelIsOpen = false;
let shopPanelIsOpen = false;
let menuIsOpen = false;
let completionCreditsPlayed = false;


function setMenuOpen(isOpen: boolean) {
  menuIsOpen = isOpen;

  if (menuPanel) {
    menuPanel.dataset.open = String(isOpen);
  }

  if (menuToggle) {
    menuToggle.setAttribute('aria-expanded', String(isOpen));
  }
}

function renderUnlockMenu() {
  if (!menuList) {
    return;
  }

  const items = getUnlockedMenuItems(inventory);

  menuList.replaceChildren(...items.map((item) => {
    const link = document.createElement('a');
    link.href = item.href;
    link.textContent = item.label;
    link.dataset.menuItem = item.id;

    if (item.external) {
      link.target = '_blank';
      link.rel = 'noreferrer';
    }

    link.addEventListener('click', () => setMenuOpen(false));
    return link;
  }));
}

function setLootPanelOpen(isOpen: boolean) {
  if (isOpen) {
    setShopPanelOpen(false);
  }

  lootPanelIsOpen = isOpen;

  if (lootPanel) {
    lootPanel.dataset.open = String(isOpen);
  }

  if (!isOpen) {
    clearRewardReveal();
  }

  if (lootToggle) {
    lootToggle.setAttribute('aria-expanded', String(isOpen));
  }
}

function setShopPanelOpen(isOpen: boolean) {
  if (isOpen) {
    setLootPanelOpen(false);
  }

  shopPanelIsOpen = isOpen;

  if (shopPanel) {
    shopPanel.dataset.open = String(isOpen);
  }

  if (shopToggle) {
    shopToggle.setAttribute('aria-expanded', String(isOpen));
  }
}

function dismissLootLabel() {
  if (!lootLabel) {
    return;
  }

  lootLabel.hidden = true;
  localStorage.setItem(lootLabelStorageKey, 'true');
}

if (lootLabel && localStorage.getItem(lootLabelStorageKey) === 'true') {
  lootLabel.hidden = true;
}

function isLootInventory(value: unknown): value is Partial<LootInventory> {
  return Boolean(value) && typeof value === 'object';
}

function loadInventory(): LootInventory {
  const savedInventory = localStorage.getItem(inventoryStorageKey);

  if (!savedInventory) {
    return { ...defaultInventory };
  }

  const parsedValue: unknown = JSON.parse(savedInventory);
  const parsedInventory = isLootInventory(parsedValue) ? parsedValue : {};

  const unlockedFeatureIds = Array.isArray(parsedInventory.unlockedFeatureIds)
    ? parsedInventory.unlockedFeatureIds.filter((featureId): featureId is string => typeof featureId === 'string')
    : [];

  const unlockedFontIds = Array.isArray(parsedInventory.unlockedFontIds)
    ? parsedInventory.unlockedFontIds.filter((fontId): fontId is string => typeof fontId === 'string')
    : [];

  const unlockedAiTipIds = Array.isArray(parsedInventory.unlockedAiTipIds)
    ? parsedInventory.unlockedAiTipIds.filter((tipId): tipId is string => typeof tipId === 'string')
    : [];

  if (typeof parsedInventory.equippedFontId === 'string' && !unlockedFontIds.includes(parsedInventory.equippedFontId)) {
    unlockedFontIds.push(parsedInventory.equippedFontId);
  }

  return {
    ...defaultInventory,
    ...parsedInventory,
    gems: Number(parsedInventory.gems ?? 0),
    openedBoxCount: Number(parsedInventory.openedBoxCount ?? 0),
    ak47Unlocked: Boolean(parsedInventory.ak47Unlocked),
    unlockedFeatureIds,
    unlockedFontIds,
    unlockedAiTipIds,
  };
}

let inventory = loadInventory();

function saveInventory() {
  localStorage.setItem(inventoryStorageKey, JSON.stringify(inventory));
}

function setHeadlineText(text: string) {
  if (!headline) {
    return;
  }

  headline.textContent = text;
  headline.dataset.text = text;
}

function renderFontSelector() {
  if (!fontSelector || !fontSelectorRow) {
    return;
  }

  const fontOptions = getUnlockedFontOptions(inventory);
  const hasUnlockedFont = fontOptions.length > 1;

  fontSelectorRow.hidden = !hasUnlockedFont;

  if (!hasUnlockedFont) {
    return;
  }

  const selectedFontId = inventory.equippedFontId ?? defaultInventory.equippedFontId ?? 'default';

  fontSelector.replaceChildren(...fontOptions.map((font) => {
    const option = document.createElement('option');
    option.value = font.id;
    option.textContent = font.label;
    option.selected = font.id === selectedFontId;
    return option;
  }));
}

function applyUnlockedFeatureClasses() {
  const unlockedFeatureIds = getUnlockedFeatureIds(inventory);
  document.documentElement.dataset.unlocks = unlockedFeatureIds.join(' ');

  for (const featureId of unlockedFeatureIds) {
    document.body.classList.add(`feature-${featureId}`);
  }
}

function isLikelyConstrainedDevice() {
  return window.innerWidth <= 820 || window.devicePixelRatio >= 2.5 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function getIntenseVisualUnlockCount() {
  return getIntenseVisualFeatureCount(getUnlockedFeatureIds(inventory));
}

function renderAiTips() {
  if (!aiTipsGrid) {
    return;
  }

  aiTipsGrid.replaceChildren(...aiTips.map((tip) => {
    const unlocked = isAiTipUnlocked(inventory, tip.id);
    const card = document.createElement('article');
    const category = document.createElement('p');
    const title = document.createElement('h3');
    const body = document.createElement('p');
    const button = document.createElement('button');

    card.className = 'ai-tip-card';
    card.dataset.category = tip.category;
    card.dataset.unlocked = String(unlocked);
    category.className = 'ai-tip-category';
    category.textContent = aiTipCategoryLabels[tip.category];
    title.textContent = unlocked ? tip.title : 'Locked field note';
    body.textContent = unlocked ? tip.body : `Spend ${tip.cost} gems to reveal a ${aiTipCategoryLabels[tip.category]} power-up.`;
    button.type = 'button';
    button.disabled = unlocked || inventory.gems < tip.cost;
    button.textContent = unlocked ? 'Unlocked ✨' : `Unlock for ${tip.cost} 💎`;
    button.addEventListener('click', () => {
      const result = purchaseAiTip(inventory, tip.id);
      inventory = { ...inventory, ...result.state };
      saveInventory();
      applyInventoryVisuals();

      if (aiGuideStatus) {
        aiGuideStatus.textContent = result.purchased
          ? `Unlocked “${tip.title}” — new AI wisdom acquired!`
          : 'Not enough gems for that locked note yet. Crack open more loot boxes!';
      }
    });

    card.append(category, title, body, button);
    return card;
  }));
}

function maybePlayCompletionCredits(previousInventory: LootInventory) {
  if (completionCreditsPlayed || !completionCredits) {
    return;
  }

  if (isCollectionComplete(previousInventory) || !isCollectionComplete(inventory)) {
    return;
  }

  completionCreditsPlayed = true;
  setLootPanelOpen(false);
  setShopPanelOpen(false);
  completionCredits.hidden = false;
  completionCredits.dataset.open = 'true';
  completionCredits.focus();
}

function closeCompletionCredits() {
  if (!completionCredits) {
    return;
  }

  completionCredits.dataset.open = 'false';
  completionCredits.hidden = true;
}

function renderShop() {
  if (!shopGrid) {
    return;
  }

  const shopItems = getShopItems(inventory);
  const progress = getCollectionProgress(inventory);

  const progressLabel = `${progress.unlocked}/${progress.total} unlocked`;

  if (shopProgress) {
    shopProgress.textContent = progressLabel;
  }

  if (shopToggleProgress) {
    shopToggleProgress.textContent = progressLabel;
  }

  if (shopGemCount) {
    shopGemCount.textContent = String(inventory.gems);
  }

  shopGrid.replaceChildren(...shopItems.map((item) => {
    const rarity = rarityMeta[item.reward.rarity];
    const card = document.createElement('article');
    const title = document.createElement('h3');
    const meta = document.createElement('p');
    const description = document.createElement('p');
    const button = document.createElement('button');

    card.className = 'shop-card';
    card.dataset.rarity = item.reward.rarity;
    card.dataset.unlocked = String(item.unlocked);
    title.textContent = item.reward.label;
    meta.textContent = `${rarity.label} · ${item.cost} gems · ${item.unlocked ? 'Unlocked' : 'Locked'}`;
    description.textContent = item.reward.description;
    button.type = 'button';
    button.disabled = item.unlocked || !item.purchasable;
    button.textContent = item.unlocked ? 'Collected' : `Buy for ${item.cost} 💎`;
    button.addEventListener('click', () => {
      const previousInventory = inventory;
      inventory = purchaseShopReward(inventory, item.reward);
      saveInventory();
      applyInventoryVisuals();
      maybePlayCompletionCredits(previousInventory);

      if (lootResult) {
        lootResult.textContent = item.unlocked ? `${item.reward.label} is already in your collection.` : `Purchased ${item.reward.label} from the gem shop.`;
        lootResult.dataset.rarity = item.reward.rarity;
      }
    });

    card.append(title, meta, description, button);
    return card;
  }));
}

// Replaced with the real starter once the backdrop renderer is set up below;
// applyInventoryVisuals uses it so the sky can switch on the moment the
// background unlock is won.
let notifyBackdropUnlockChanged: () => void = () => {};

// Latched by the backdrop renderer when frames stay slow even at its minimum
// resolution: the device cannot afford the full effect stack this session.
let liteModeForced = false;

let displayedGems: number | null = null;
let gemCounterFrame = 0;

function animateGemCounter(target: number) {
  if (!gemCount) {
    return;
  }

  cancelAnimationFrame(gemCounterFrame);

  const from = displayedGems;

  if (from === null || from === target || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    displayedGems = target;
    gemCount.textContent = String(target);
    return;
  }

  const startTime = performance.now();
  const duration = Math.min(900, 340 + Math.abs(target - from) * 10);
  gemCount.classList.add('is-counting');

  const step = (now: number) => {
    const progress = Math.min(1, (now - startTime) / duration);
    const eased = 1 - (1 - progress) ** 3;
    displayedGems = Math.round(from + (target - from) * eased);
    gemCount.textContent = String(displayedGems);

    if (progress < 1) {
      gemCounterFrame = requestAnimationFrame(step);
    } else {
      gemCount.classList.remove('is-counting');
    }
  };

  gemCounterFrame = requestAnimationFrame(step);
}

function applyInventoryVisuals() {
  applyUnlockedFeatureClasses();
  document.body.classList.toggle('performance-lite', liteModeForced || (isLikelyConstrainedDevice() && getIntenseVisualUnlockCount() >= 4));

  animateGemCounter(inventory.gems);

  if (lootCombo) {
    const nextStreak = (inventory.openedBoxCount % 5) + 1;
    lootCombo.textContent = nextStreak === 5
      ? 'Next box: streak jackpot!'
      : `Streak ${nextStreak}/5 to jackpot`;
  }

  document.body.classList.toggle('loot-font-mono', inventory.equippedFontId === 'font-mono');
  document.body.classList.toggle('loot-font-arcade', inventory.equippedFontId === 'font-arcade');
  document.body.classList.toggle('loot-font-comic', inventory.equippedFontId === 'font-comic');
  document.body.classList.toggle('loot-font-wizard', inventory.equippedFontId === 'font-wizard');

  const headlineReward = lootRewards.find((reward) => reward.id === inventory.equippedHeadlineId);
  setHeadlineText(headlineReward?.kind === 'headline' ? String(headlineReward.value) : 'hello world');

  renderFontSelector();
  renderShop();
  renderAiTips();
  renderUnlockMenu();

  if (shopLauncher) {
    shopLauncher.hidden = !hasUnlockedFeature(inventory, 'shop');
  }

  if (akLauncher) {
    akLauncher.hidden = !inventory.ak47Unlocked;
  }

  if (akEquipButton) {
    akEquipButton.setAttribute('aria-pressed', String(akEquipped));
    akEquipButton.textContent = akEquipped ? 'AK-47 equipped — tap UI' : 'Equip AK-47';
  }

  notifyBackdropUnlockChanged();
}

function describeReward(reward: LootReward): string {
  if (reward.kind === 'fortune') {
    return `${getRevealAnnouncement(reward)} It reads: “${String(reward.value)}”`;
  }

  return `${getRevealAnnouncement(reward)} ${reward.description}`;
}

function getRewardRevealTarget(reward: LootReward): HTMLElement | null {
  if (reward.kind === 'feature') {
    const selectorByFeatureId: Record<string, string> = {
      'animated-title': '#headline',
      'hero-copy': '.hero-copy',
      actions: '.hero-actions',
      'status-grid': '.status-grid',
      'demo-panel': '.demo-panel',
      'orbital-ring': '.orbital-ring',
      eyebrow: '.eyebrow',
      'ai-guide': '.ai-guide-panel',
      'ufo-pet': '.ufo-pet',
      'color-wheel': '.color-wheel-corner',
      shop: '.shop-launcher',
      gems: '.loot-wallet',
    };

    const selector = selectorByFeatureId[String(reward.value)];
    return selector ? document.querySelector<HTMLElement>(selector) : null;
  }

  if (reward.kind === 'font' || reward.kind === 'headline') {
    return headline;
  }

  if (reward.kind === 'background') {
    return document.querySelector<HTMLElement>('.hero-card');
  }

  return lootResult;
}

function hideLootPanelForRewardReveal() {
  lootPanelIsOpen = false;

  if (lootPanel) {
    lootPanel.dataset.open = 'false';
  }

  if (lootToggle) {
    lootToggle.setAttribute('aria-expanded', 'false');
  }
}

function renderRevealBurst(theme: RevealTheme, reduceMotion: boolean) {
  if (!lootRevealBurst) {
    return;
  }

  if (reduceMotion) {
    lootRevealBurst.replaceChildren();
    return;
  }

  const particles = createRevealParticles(getSpinSeed(), theme.particleCount);

  lootRevealBurst.replaceChildren(...particles.map((particle) => {
    const spark = document.createElement('i');
    spark.style.setProperty('--particle-angle', `${particle.angleDeg}deg`);
    spark.style.setProperty('--particle-distance', `${particle.distancePx}px`);
    spark.style.setProperty('--particle-size', `${particle.sizePx}px`);
    spark.style.setProperty('--particle-delay', `${particle.delayMs}ms`);
    spark.style.setProperty('--particle-duration', `${particle.durationMs}ms`);
    return spark;
  }));
}

function flashRarityScreen(theme: RevealTheme, reduceMotion: boolean) {
  if (!rarityFlash || reduceMotion) {
    return;
  }

  rarityFlash.style.setProperty('--flash-rgb', theme.rgb);
  rarityFlash.style.setProperty('--flash-peak', String(theme.flashPeakOpacity));
  rarityFlash.classList.remove('is-flashing');
  void rarityFlash.offsetWidth;
  rarityFlash.classList.add('is-flashing');
}

function shakeScreen(theme: RevealTheme, reduceMotion: boolean) {
  if (!appShell || reduceMotion || theme.shakeIntensityPx <= 0) {
    return;
  }

  appShell.style.setProperty('--shake-distance', `${theme.shakeIntensityPx}px`);
  appShell.classList.remove('is-shaking');
  void appShell.offsetWidth;
  appShell.classList.add('is-shaking');
}

async function playRewardReveal(reward: LootReward, reduceMotion: boolean) {
  if (!lootReveal || reward.kind === 'gems') {
    return;
  }

  const theme = getRevealTheme(reward.rarity);
  const target = getRewardRevealTarget(reward);

  lootReveal.hidden = false;
  lootReveal.dataset.rarity = reward.rarity;
  lootReveal.style.setProperty('--reveal-rgb', theme.rgb);
  lootReveal.style.setProperty('--reveal-stagger', `${theme.staggerMs}ms`);

  if (lootRevealRarity) {
    lootRevealRarity.textContent = theme.kicker;
  }

  if (lootRevealTitle) {
    lootRevealTitle.textContent = reward.label;
  }

  if (lootRevealDescription) {
    // Fortunes reveal their actual prophecy — that is the prize.
    lootRevealDescription.textContent = reward.kind === 'fortune' ? `“${String(reward.value)}”` : reward.description;
  }

  if (lootRevealFlavor) {
    lootRevealFlavor.textContent = theme.flavor;
  }

  lootRevealFx?.style.setProperty('--reveal-rgb', theme.rgb);

  if (lootRevealShockwave) {
    lootRevealShockwave.dataset.waves = String(theme.shockwaveCount);
  }

  if (!reduceMotion) {
    const panelAnimation = lootPanel?.animate([
      { opacity: 1, transform: 'translate(-50%, 0) scale(1)', filter: 'blur(0)' },
      { opacity: 0, transform: 'translate(-50%, 10px) scale(0.92)', filter: 'blur(12px)' },
    ], {
      duration: 420,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      fill: 'both',
    });
    await waitForAnimationCompletion(panelAnimation);
  }

  hideLootPanelForRewardReveal();
  flashRarityScreen(theme, reduceMotion);
  shakeScreen(theme, reduceMotion);
  renderRevealBurst(theme, reduceMotion);

  if (lootRevealShockwave && !reduceMotion) {
    lootRevealShockwave.classList.remove('is-rippling');
    void lootRevealShockwave.offsetWidth;
    lootRevealShockwave.classList.add('is-rippling');
  }

  playFanfare(createRarityFanfare(reward.rarity));
  target?.classList.add('unlock-reveal-focus');
  target?.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });

  if (reduceMotion) {
    return;
  }

  navigator.vibrate?.([10, 30, 10 + theme.shakeIntensityPx * 6]);

  const revealAnimation = lootReveal.animate([
    { opacity: 0, transform: 'translate(-50%, -42%) scale(0.82) rotateX(14deg)', filter: 'blur(14px) brightness(2)' },
    { opacity: 1, transform: 'translate(-50%, -53%) scale(1.06) rotateX(0deg)', filter: 'blur(0) brightness(1.25)', offset: 0.58 },
    { opacity: 1, transform: 'translate(-50%, -50%) scale(1) rotateX(0deg)', filter: 'blur(0) brightness(1)' },
  ], {
    duration: theme.revealDurationMs,
    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    fill: 'both',
  });

  await waitForAnimationCompletion(revealAnimation);
}

function clearRewardReveal() {
  lootReveal?.setAttribute('hidden', '');
  lootReveal?.getAnimations().forEach((animation) => animation.cancel());
  lootPanel?.getAnimations().forEach((animation) => animation.cancel());
  lootRevealBurst?.replaceChildren();
  lootRevealShockwave?.classList.remove('is-rippling');
  appShell?.classList.remove('is-shaking');
  document.querySelectorAll('.unlock-reveal-focus').forEach((element) => element.classList.remove('unlock-reveal-focus'));
}

function continueFromRewardReveal() {
  clearRewardReveal();
  setLootPanelOpen(true);
}

function renderLootReel(items: readonly LootTickerItem[]): HTMLElement | undefined {
  if (!lootReel) {
    return undefined;
  }

  const track = document.createElement('div');
  track.className = 'loot-reel-track';
  track.replaceChildren(...items.map(({ reward }) => {
    const rarity = rarityMeta[reward.rarity];
    const item = document.createElement('span');
    const rarityBadge = document.createElement('strong');
    const rewardLabel = document.createElement('em');

    item.dataset.rarity = reward.rarity;
    item.setAttribute('aria-label', `${rarity.label} preview: ${reward.label}`);
    rarityBadge.textContent = rarity.label;
    rewardLabel.textContent = reward.label;
    item.append(rarityBadge, rewardLabel);
    return item;
  }));
  lootReel.replaceChildren(track);
  return track;
}

function grantReward(reward: LootReward) {
  const isFirstGemReward = reward.kind === 'gems' && inventory.gems === 0;
  const openedBoxCount = inventory.openedBoxCount + 1;
  const comboBonus = getLootComboBonus(openedBoxCount, reward.rarity);
  const previousInventory = inventory;
  inventory = applyRewardToInventory({ ...inventory, openedBoxCount }, reward);

  if (comboBonus.isJackpot && hasUnlockedFeature(inventory, 'gems')) {
    inventory = { ...inventory, gems: inventory.gems + comboBonus.gems };
  }

  saveInventory();
  applyInventoryVisuals();

  if (lootResult) {
    const rewardText = isFirstGemReward
      ? 'Ooh, some shiny gems… they might come in handy later…'
      : describeReward(reward);
    const comboText = comboBonus.isJackpot && hasUnlockedFeature(inventory, 'gems')
      ? ` Streak jackpot: +${comboBonus.gems} bonus gems!`
      : ` Streak ${comboBonus.streak}/5 charged.`;
    lootResult.textContent = `${rewardText}${comboText}`;
    lootResult.dataset.rarity = reward.rarity;
  }

  maybePlayCompletionCredits(previousInventory);
}

function startReelTickLoop(lootTrack: HTMLElement | undefined, targetTranslate: number): () => void {
  if (!lootTrack || targetTranslate >= 0) {
    return () => {};
  }

  const firstItem = lootTrack.children.item(0) as HTMLElement | null;
  const secondItem = lootTrack.children.item(1) as HTMLElement | null;
  const stride = firstItem && secondItem ? Math.max(1, secondItem.offsetLeft - firstItem.offsetLeft) : 158;
  let active = true;
  let previousIndex = 0;

  const sample = () => {
    if (!active) {
      return;
    }

    const { transform } = getComputedStyle(lootTrack);
    const translateX = transform && transform !== 'none' ? new DOMMatrixReadOnly(transform).m41 : 0;
    const index = Math.floor(-translateX / stride);

    if (index !== previousIndex) {
      previousIndex = index;
      const progress = Math.min(1, translateX / targetTranslate);
      playTick(220 + Math.round(progress * 420), 0.02);
      lootReel?.animate([
        { filter: 'brightness(1)' },
        { filter: 'brightness(1.3)' },
        { filter: 'brightness(1)' },
      ], { duration: 90 });

      if (progress > 0.7) {
        navigator.vibrate?.(4);
      }
    }

    requestAnimationFrame(sample);
  };

  requestAnimationFrame(sample);

  return () => {
    active = false;
  };
}

async function openLootBox() {
  if (!lootButton || lootIsOpening) {
    return;
  }

  lootIsOpening = true;
  try {
    clearRewardReveal();
    lootButton.disabled = true;
    lootButton.dataset.opening = 'true';

    const eligibleRewards = getEligibleLootRewards(inventory);
    const plan = createLootSpinPlan(getSpinSeed(), lootRotation, eligibleRewards, getSpinSeed());
    const reduceLootMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animationProfile = getLootAnimationProfile(plan.reward.rarity);
    const lootTrack = renderLootReel(plan.tickerItems);

    lootButton.dataset.rarity = plan.reward.rarity;
    if (lootReel) {
      lootReel.dataset.spinning = 'true';
      lootReel.dataset.rarity = plan.reward.rarity;
    }

    if (lootResult) {
      lootResult.textContent = 'Loot box overclocking the reward matrix…';
      lootResult.dataset.rarity = 'common';
    }

    if (reduceLootMotion) {
      lootRotation = plan.finalRotationDegrees;
      grantReward(plan.reward);
    } else {
      const boxAnimation = lootButton.animate([
        { transform: 'translate3d(0, 0, 0) rotateZ(0deg) scale(1)', filter: 'brightness(1) saturate(1)' },
        { transform: `translate3d(0, -${animationProfile.chestLiftPx}px, 0) rotateZ(${-animationProfile.chestShakeDegrees}deg) scale(1.04)`, filter: 'brightness(1.45) saturate(1.4)', offset: 0.18 },
        { transform: `translate3d(0, 9px, 0) rotateZ(${animationProfile.chestShakeDegrees}deg) scale(0.98)`, filter: 'brightness(1.25) saturate(1.3)', offset: 0.36 },
        { transform: `translate3d(0, -${Math.round(animationProfile.chestLiftPx * 0.65)}px, 0) rotateZ(${-animationProfile.chestShakeDegrees / 2}deg) scale(${animationProfile.burstScale})`, filter: 'brightness(2.1) saturate(2.2)', offset: 0.62 },
        { transform: 'translate3d(0, 0, 0) rotateZ(0deg) scale(1)', filter: 'brightness(1.15) saturate(1.2)' },
      ], {
        duration: plan.durationMs,
        easing: plan.easing.css,
      });

      const targetPreview = lootTrack?.children.item(plan.targetTickerIndex) as HTMLElement | null;
      const reelBounds = lootReel?.getBoundingClientRect();
      const targetTranslate = targetPreview && reelBounds
        ? (reelBounds.width / 2) - targetPreview.offsetLeft - (targetPreview.offsetWidth / 2)
        : 0;
      const reelAnimation = lootTrack?.animate([
        { transform: 'translateX(0) scale(1)', filter: 'blur(0) saturate(1)', offset: 0 },
        { transform: `translateX(${targetTranslate * 0.74}px) scale(1.012)`, filter: 'blur(2px) saturate(2.35)', offset: 0.66 },
        { transform: `translateX(${targetTranslate + 18}px) scale(1.006)`, filter: 'blur(0.6px) saturate(1.7)', offset: 0.88 },
        { transform: `translateX(${targetTranslate}px) scale(1)`, filter: 'blur(0) saturate(1.18)' },
      ], {
        duration: plan.durationMs,
        easing: plan.easing.css,
        fill: 'forwards',
      });

      const stopReelTicks = startReelTickLoop(lootTrack, targetTranslate);

      await Promise.all([
        waitForAnimationCompletion(boxAnimation),
        waitForAnimationCompletion(reelAnimation),
      ]);
      stopReelTicks();
      lootRotation = plan.finalRotationDegrees;
      targetPreview?.classList.add('is-selected');
      grantReward(plan.reward);
      await playRewardReveal(plan.reward, reduceLootMotion);
    }

    if (reduceLootMotion) {
      await playRewardReveal(plan.reward, reduceLootMotion);
    }
  } finally {
    lootButton.dataset.opening = 'false';
    delete lootButton.dataset.rarity;
    if (lootReel) {
      lootReel.dataset.spinning = 'false';
    }
    lootButton.disabled = false;
    lootIsOpening = false;
  }
}

function equipAk47() {
  if (!inventory.ak47Unlocked) {
    return;
  }

  akEquipped = !akEquipped;
  document.documentElement.classList.toggle('ak-equipped', akEquipped);
  applyInventoryVisuals();

  if (lootResult) {
    lootResult.textContent = akEquipped ? 'AK-47 equipped. Tap or click UI pieces to leave bullet holes and break panels.' : 'AK-47 holstered.';
    lootResult.dataset.rarity = 'mythic';
  }
}

function createBulletHole(x: number, y: number) {
  const hole = document.createElement('span');
  hole.className = 'bullet-hole';
  hole.style.left = `${x}px`;
  hole.style.top = `${y}px`;
  document.body.append(hole);
}

function damageUiAtPoint(event: PointerEvent) {
  if (!akEquipped || (event.target instanceof Element && event.target.closest('.loot-box-panel, .loot-chest-launcher, .shop-panel, .shop-launcher, .ak-launcher'))) {
    return;
  }

  createBulletHole(event.pageX, event.pageY);

  const targetElement = document.elementFromPoint(event.clientX, event.clientY)?.closest('.hero-card, .status-grid article, .demo-panel, .ai-guide-panel, .button, .eyebrow, .color-wheel-corner');

  if (!targetElement) {
    return;
  }

  const hits = (shotCounts.get(targetElement) ?? 0) + 1;
  shotCounts.set(targetElement, hits);
  targetElement.classList.add('ui-damaged');

  if (hits >= 3) {
    targetElement.classList.add('ui-destroyed');
  }
}

inventory = applyDebugGemsFromSearch(inventory, window.location.search);

if (new URLSearchParams(window.location.search).get('gems') === '10000') {
  saveInventory();

  if (lootResult) {
    lootResult.textContent = 'Debug wallet granted: Gem shop unlocked with 10000 gems via ?gems=10000.';
    lootResult.dataset.rarity = 'rare';
  }
}

if (shouldGrantAkFromSearch(window.location.search)) {
  inventory = { ...inventory, ak47Unlocked: true };
  saveInventory();

  if (lootResult) {
    lootResult.textContent = 'Debug drop granted: AK-47 unlocked via ?ak=47.';
    lootResult.dataset.rarity = 'mythic';
  }
}

applyInventoryVisuals();

if (menuToggle) {
  menuToggle.addEventListener('click', () => {
    setMenuOpen(!menuIsOpen);
  });
}

if (lootToggle) {
  lootToggle.addEventListener('click', () => {
    setLootPanelOpen(!lootPanelIsOpen);
  });
}

if (lootCloseButton) {
  lootCloseButton.addEventListener('click', () => {
    setLootPanelOpen(false);
  });
}

if (shopToggle) {
  shopToggle.addEventListener('click', () => {
    setShopPanelOpen(!shopPanelIsOpen);
  });
}

if (shopCloseButton) {
  shopCloseButton.addEventListener('click', () => {
    setShopPanelOpen(false);
  });
}

if (lootLabelDismissButton) {
  lootLabelDismissButton.addEventListener('click', dismissLootLabel);
}

if (lootButton) {
  lootButton.addEventListener('click', () => {
    setLootPanelOpen(true);
    void openLootBox();
  });
}

fontSelector?.addEventListener('change', () => {
  inventory = {
    ...inventory,
    equippedFontId: fontSelector.value === 'default' ? undefined : fontSelector.value,
  };
  saveInventory();
  applyInventoryVisuals();
});

akEquipButton?.addEventListener('click', equipAk47);
completionCloseButton?.addEventListener('click', closeCompletionCredits);
completionCredits?.addEventListener('click', (event) => {
  if (event.target === completionCredits) {
    closeCompletionCredits();
  }
});
lootReveal?.addEventListener('click', continueFromRewardReveal);
rarityFlash?.addEventListener('animationend', () => rarityFlash.classList.remove('is-flashing'));
lightingWash?.addEventListener('animationend', () => lightingWash.classList.remove('is-pulsing'));
appShell?.addEventListener('animationend', (event) => {
  if (event.target === appShell) {
    appShell.classList.remove('is-shaking');
  }
});
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') {
    return;
  }

  if (lootReveal && !lootReveal.hidden) {
    continueFromRewardReveal();
    return;
  }

  if (lootPanelIsOpen) {
    setLootPanelOpen(false);
  }

  if (shopPanelIsOpen) {
    setShopPanelOpen(false);
  }

  if (menuIsOpen) {
    setMenuOpen(false);
  }
});
document.addEventListener('pointerdown', damageUiAtPoint);




/*
 * Cosmic backdrop: a lightweight Canvas2D aurora + drifting starfield.
 * It only runs once the background unlock is earned, renders at a capped
 * pixel ratio under a frame budget, drops resolution on sustained slow
 * frames, pauses in hidden tabs, and collapses to one static frame under
 * reduced motion.
 */
const canvas = document.querySelector<HTMLCanvasElement>('#cosmic-canvas');

if (!canvas) {
  throw new Error('Cosmic canvas is missing from the document.');
}

const backdropCanvas: HTMLCanvasElement = canvas;
const backdropContext = backdropCanvas.getContext('2d');
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const performanceProfile = getScenePerformanceProfile(reduceMotion, {
  constrainedDevice: isLikelyConstrainedDevice(),
  intenseVisualFeatureCount: getIntenseVisualUnlockCount(),
});

// Fixed seeds keep the same sky between visits while staying deterministic.
const stars = createStarField(0.427, getStarCountForPointBudget(performanceProfile.galaxyPointCount));
const auroraBands = createAuroraBands(0.68);

const starTintColors: Record<StarTint, string> = {
  white: '255, 255, 255',
  cyan: '105, 247, 255',
  magenta: '255, 79, 216',
  gold: '255, 209, 102',
};

function createStarSprite(rgb: string): HTMLCanvasElement {
  const sprite = document.createElement('canvas');
  sprite.width = 32;
  sprite.height = 32;
  const spriteContext = sprite.getContext('2d');

  if (spriteContext) {
    const gradient = spriteContext.createRadialGradient(16, 16, 0, 16, 16, 16);
    gradient.addColorStop(0, `rgba(${rgb}, 1)`);
    gradient.addColorStop(0.25, `rgba(${rgb}, 0.9)`);
    gradient.addColorStop(0.6, `rgba(${rgb}, 0.22)`);
    gradient.addColorStop(1, `rgba(${rgb}, 0)`);
    spriteContext.fillStyle = gradient;
    spriteContext.fillRect(0, 0, 32, 32);
  }

  return sprite;
}

const starSprites = Object.fromEntries(
  Object.entries(starTintColors).map(([tint, rgb]) => [tint, createStarSprite(rgb)]),
) as Record<StarTint, HTMLCanvasElement>;

// The aurora paints onto a thumbnail-sized offscreen canvas that gets
// scaled up by the compositor: huge soft gradients at postage-stamp cost.
const auroraCanvas = document.createElement('canvas');
auroraCanvas.width = 96;
auroraCanvas.height = 54;
const auroraContext = auroraCanvas.getContext('2d');
const auroraBaseColors = ['105, 247, 255', '255, 79, 216', '99, 91, 255'];

function parseRgbTriple(rgb: string): [number, number, number] {
  const [r = 105, g = 247, b = 255] = rgb.split(',').map((part) => Number(part.trim()));
  return [r, g, b];
}

function mixRgbTriples(base: string, accent: string, amount: number): string {
  const from = parseRgbTriple(base);
  const to = parseRgbTriple(accent);
  return from.map((channel, index) => Math.round(channel + (to[index] - channel) * amount)).join(', ');
}

function getBackdropAccentRgb(): string {
  const accent = document.documentElement.style.getPropertyValue('--wheel-glow-rgb').trim();
  return accent || '105, 247, 255';
}

function drawAurora(elapsedSeconds: number) {
  if (!auroraContext) {
    return;
  }

  const { width, height } = auroraCanvas;
  const accent = inventory.equippedBackgroundId === 'disco'
    ? getDiscoAccentRgb(elapsedSeconds)
    : getBackdropAccentRgb();
  auroraContext.clearRect(0, 0, width, height);
  auroraContext.globalCompositeOperation = 'lighter';

  auroraBands.forEach((band, index) => {
    const drift = getAuroraDrift(band, elapsedSeconds);
    const centerX = (band.anchorX + drift.x) * width;
    const centerY = (band.anchorY + drift.y) * height;
    const radius = band.radiusScale * Math.max(width, height) * 0.7;
    const rgb = mixRgbTriples(auroraBaseColors[index % auroraBaseColors.length], accent, band.accentMix);
    const gradient = auroraContext.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);

    gradient.addColorStop(0, `rgba(${rgb}, ${band.alpha})`);
    gradient.addColorStop(0.6, `rgba(${rgb}, ${band.alpha * 0.4})`);
    gradient.addColorStop(1, `rgba(${rgb}, 0)`);
    auroraContext.fillStyle = gradient;
    auroraContext.fillRect(0, 0, width, height);
  });
}

interface ActiveComet {
  spawn: CometSpawn;
  startedAt: number;
}

let activeComets: ActiveComet[] = [];
let nextCometAt = 0;

function drawComets(context: CanvasRenderingContext2D, timestamp: number, ratio: number) {
  if (timestamp >= nextCometAt) {
    activeComets.push({ spawn: createCometSpawn(getSpinSeed()), startedAt: timestamp });
    nextCometAt = timestamp + getCometIntervalMs(getSpinSeed(), inventory.equippedBackgroundId === 'comets');
  }

  if (activeComets.length === 0) {
    return;
  }

  const width = backdropCanvas.width;
  const height = backdropCanvas.height;

  activeComets = activeComets.filter((comet) => timestamp - comet.startedAt <= comet.spawn.durationMs);

  for (const comet of activeComets) {
    const progress = Math.min(1, (timestamp - comet.startedAt) / comet.spawn.durationMs);
    const travel = comet.spawn.lengthScale * 2.4 * progress;
    const headX = (comet.spawn.startX + Math.cos(comet.spawn.angleRad) * travel) * width;
    const headY = (comet.spawn.startY + Math.sin(comet.spawn.angleRad) * travel) * height;
    const tailX = headX - Math.cos(comet.spawn.angleRad) * comet.spawn.lengthScale * width;
    const tailY = headY - Math.sin(comet.spawn.angleRad) * comet.spawn.lengthScale * width;
    const opacity = getCometOpacity(progress);
    const gradient = context.createLinearGradient(tailX, tailY, headX, headY);

    gradient.addColorStop(0, 'rgba(105, 247, 255, 0)');
    gradient.addColorStop(0.7, `rgba(105, 247, 255, ${0.35 * opacity})`);
    gradient.addColorStop(1, `rgba(255, 255, 255, ${0.9 * opacity})`);
    context.strokeStyle = gradient;
    context.lineWidth = 1.6 * ratio;
    context.lineCap = 'round';
    context.beginPath();
    context.moveTo(tailX, tailY);
    context.lineTo(headX, headY);
    context.stroke();
  }
}

const pointer = { x: 0, y: 0 };
const pointerTarget = { x: 0, y: 0 };

window.addEventListener('pointermove', (event) => {
  pointerTarget.x = mapRange(event.clientX, 0, window.innerWidth, -1, 1);
  pointerTarget.y = mapRange(event.clientY, 0, window.innerHeight, -1, 1);
}, { passive: true });

let lastScrollTimestamp = Number.NEGATIVE_INFINITY;

window.addEventListener('scroll', () => {
  lastScrollTimestamp = performance.now();
}, { passive: true });

const initialPixelRatio = getOptimizedPixelRatio(window.devicePixelRatio, performanceProfile.maxPixelRatio);
const minimumBackdropPixelRatio = 0.5;
let adaptiveRenderState = createAdaptiveRenderState(initialPixelRatio);
let backdropRunning = false;
let backdropStartedAt = 0;
let lastFrameTimestamp = 0;
let floorSlowFrameCount = 0;

function forcePerformanceLite() {
  if (liteModeForced) {
    return;
  }

  liteModeForced = true;
  document.body.classList.add('performance-lite');
}

function resizeBackdropCanvas() {
  backdropCanvas.width = Math.max(1, Math.round(window.innerWidth * adaptiveRenderState.pixelRatio));
  backdropCanvas.height = Math.max(1, Math.round(window.innerHeight * adaptiveRenderState.pixelRatio));
}

function drawBackdropFrame(elapsedSeconds: number, introProgress: number, timestamp: number) {
  if (!backdropContext) {
    return;
  }

  const ratio = adaptiveRenderState.pixelRatio;
  const width = backdropCanvas.width;
  const height = backdropCanvas.height;

  backdropContext.clearRect(0, 0, width, height);
  backdropContext.globalAlpha = 0.85 * introProgress;
  backdropContext.drawImage(auroraCanvas, 0, 0, width, height);

  const warpEquipped = inventory.equippedBackgroundId === 'warp';
  const driftSpeed = warpEquipped ? 0.03 : 0.0022;

  for (const star of stars) {
    const drift = (star.x + elapsedSeconds * driftSpeed * (0.25 + star.depth * 0.75)) % 1;
    const twinkle = getStarTwinkle(star.twinklePhase, star.twinkleSpeed, elapsedSeconds);
    const size = star.radius * 5.5 * ratio;
    const x = drift * width + getParallaxShift(star.depth, pointer.x) * ratio;
    const y = star.y * height + getParallaxShift(star.depth, pointer.y, 16) * ratio;

    backdropContext.globalAlpha = twinkle * introProgress * (0.35 + star.depth * 0.65);

    if (warpEquipped) {
      const streak = size * getWarpStreakScale(star.depth);
      backdropContext.drawImage(starSprites[star.tint], x - streak, y - size * 0.28, streak, size * 0.56);
    } else {
      backdropContext.drawImage(starSprites[star.tint], x - size / 2, y - size / 2, size, size);
    }
  }

  backdropContext.globalAlpha = introProgress;
  drawComets(backdropContext, timestamp, ratio);
  backdropContext.globalAlpha = 1;
}

function backdropFrame(timestamp: number) {
  if (!backdropRunning) {
    return;
  }

  requestAnimationFrame(backdropFrame);

  if (document.hidden) {
    lastFrameTimestamp = timestamp;
    return;
  }

  const isScrolling = timestamp - lastScrollTimestamp < 180;

  if (!shouldRenderFrame(
    timestamp,
    lastFrameTimestamp,
    performanceProfile.frameIntervalMs,
    performanceProfile.scrollFrameIntervalMs,
    isScrolling,
  )) {
    return;
  }

  const frameDelta = timestamp - lastFrameTimestamp;
  lastFrameTimestamp = timestamp;

  const nextAdaptiveRenderState = getNextAdaptiveRenderState(adaptiveRenderState, frameDelta, minimumBackdropPixelRatio);
  if (nextAdaptiveRenderState.pixelRatio !== adaptiveRenderState.pixelRatio) {
    adaptiveRenderState = nextAdaptiveRenderState;
    resizeBackdropCanvas();
  } else {
    adaptiveRenderState = nextAdaptiveRenderState;
  }

  // Still dropping frames at the lowest canvas resolution? The CSS effect
  // stack is what this device cannot afford — latch performance-lite.
  if (!liteModeForced && adaptiveRenderState.pixelRatio <= minimumBackdropPixelRatio) {
    floorSlowFrameCount = frameDelta > 45 ? floorSlowFrameCount + 1 : 0;

    if (floorSlowFrameCount >= 12) {
      forcePerformanceLite();
    }
  }

  const elapsedSeconds = (timestamp - backdropStartedAt) / 1000;
  const introProgress = 1 - (1 - getCinematicIntroProgress(elapsedSeconds)) ** 3;

  pointer.x += (pointerTarget.x - pointer.x) * 0.045;
  pointer.y += (pointerTarget.y - pointer.y) * 0.045;

  drawAurora(elapsedSeconds);
  drawBackdropFrame(elapsedSeconds, introProgress, timestamp);
}

function startBackdrop() {
  if (!hasUnlockedFeature(inventory, 'background-animation')) {
    return;
  }

  if (reduceMotion) {
    resizeBackdropCanvas();
    drawAurora(20);
    drawBackdropFrame(20, 1, 0);
    return;
  }

  if (backdropRunning) {
    return;
  }

  backdropRunning = true;
  backdropStartedAt = performance.now();
  lastFrameTimestamp = 0;
  resizeBackdropCanvas();
  requestAnimationFrame(backdropFrame);
}

window.addEventListener('resize', () => {
  resizeBackdropCanvas();

  if (reduceMotion) {
    startBackdrop();
  }
}, { passive: true });

notifyBackdropUnlockChanged = startBackdrop;
startBackdrop();

/*
 * Stardust Cursor unlock: a short glittering trail behind the pointer.
 * DOM sparks are throttled, capped, and self-removing, and stand down
 * under reduced motion or performance-lite.
 */
const cursorSparkColors = ['#69f7ff', '#ff4fd8', '#ffd166', '#ffffff'];
let lastCursorSparkAt = 0;
let liveCursorSparkCount = 0;

document.addEventListener('pointermove', (event) => {
  if (!document.body.classList.contains('feature-cursor-trail')
    || document.body.classList.contains('performance-lite')
    || reduceMotion) {
    return;
  }

  const now = performance.now();

  if (now - lastCursorSparkAt < 40 || liveCursorSparkCount >= 24) {
    return;
  }

  lastCursorSparkAt = now;
  liveCursorSparkCount += 1;

  const spark = document.createElement('span');
  spark.className = 'cursor-spark';
  spark.style.left = `${event.clientX}px`;
  spark.style.top = `${event.clientY}px`;
  spark.style.setProperty('--spark-color', cursorSparkColors[Math.floor(Math.random() * cursorSparkColors.length)]);
  spark.style.setProperty('--spark-dx', `${Math.round((Math.random() - 0.5) * 64)}px`);
  spark.style.setProperty('--spark-dy', `${Math.round(18 + Math.random() * 42)}px`);
  spark.addEventListener('animationend', () => {
    spark.remove();
    liveCursorSparkCount -= 1;
  });
  document.body.append(spark);
}, { passive: true });
